import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import DraggablePanel from "./DraggablePanel";
import Button from "../ui/Button";
import Separator from "../ui/Separator";
import Checkbox from "../ui/Checkbox";
import AxisNumberInput from "../ui/AxisNumberInput";
import FieldLabel from "../ui/FieldLabel";
import MenuSelect from "../ui/MenuSelect";
import { getSceneState, useBodies, useSites, useWorldBodies } from "../../stores/sceneStore";
import { useViewerStore } from "../../stores/viewerStore";
import { useGraspStore } from "../../stores/graspStore";
import { useSelectionStore } from "../../stores/selectionStore";
import { useIkMarkerVisible, useIkTargetActions } from "../../stores/ikTargetStore";
import { useObjectRegistry } from "../../runtime/objectRegistry";
import { logger } from "../../utils/logger";
import { buildIkChainFromScene, getLastIkChainBuildFailureReason } from "../../ik/chainBuilder";

const formatCoord = (v) => (Number.isFinite(v) ? v.toFixed(4) : "0.0000");

const IkTargetPanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const [editCoords, setEditCoords] = useState([0, 0, 0]);
  const [editingInputs, setEditingInputs] = useState({});
  const [ikWarning, setIkWarning] = useState("");
  const [pickPlaceStatus, setPickPlaceStatus] = useState("");
  const markerVisible = useIkMarkerVisible();
  const ikTargetActions = useIkTargetActions();
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const sites = useSites();
  const sceneObjects = useObjectRegistry();
  const showGrasps = useViewerStore((s) => s.showGrasps);
  const toggleShowGrasps = useViewerStore((s) => s.actions.toggleShowGrasps);
  const graspsByBodyUuid = useGraspStore((s) => s.graspsByBodyUuid);
  const selectedBody = useSelectionStore((s) => s.selectedBody);
  const [endEffectorChoice, setEndEffectorChoice] = useState("");
  const [targetWorldUuid, setTargetWorldUuid] = useState("");
  const [dropoffWorldUuid, setDropoffWorldUuid] = useState("");
  const GRIPPER_SITE_NAME = "gripper_target";

  const gripperTarget = useMemo(() => {
    const siteEntries = Object.values(sites || {});
    const site = siteEntries.find((entry) => entry?.name === GRIPPER_SITE_NAME) || null;
    if (!site) return null;
    const siteUuid = site.uuid;
    const bodyEntry = Object.values(bodies || {}).find((b) =>
      Array.isArray(b?.sites) ? b.sites.includes(siteUuid) : false
    );
    if (!bodyEntry) return null;
    return {
      siteUuid,
      bodyUuid: bodyEntry.uuid,
      bodyName: bodyEntry.name || null,
    };
  }, [sites, bodies]);

  useEffect(() => {
    // Default marker visibility when the panel is active.
    ikTargetActions.setMarkerVisible(false);
    window.dispatchEvent(
      new CustomEvent("ik-target-marker-visibility", { detail: { visible: false } })
    );
  }, [ikTargetActions]);

  // Live-update marker while editing coordinates (does NOT trigger IK solve).
  useEffect(() => {
    if (!open) return;
    const [x, y, z] = editCoords;
    ikTargetActions.setMarkerWorld([x, y, z]);
  }, [open, editCoords, ikTargetActions]);

  useEffect(() => {
    if (gripperTarget) {
      setEndEffectorChoice("gripper_target");
      return;
    }
    if (selectedBody) {
      setEndEffectorChoice(selectedBody);
      return;
    }
    // fallback to deepest body
    const roots = (worldBodies || [])
      .map((wb) => (typeof wb === "string" ? wb : wb?.uuid))
      .filter(Boolean);
    let best = { depth: -1, uuid: "" };
    const stack = roots.map((uuid) => ({ uuid, depth: 0 }));
    while (stack.length) {
      const { uuid, depth } = stack.pop();
      if (depth > best.depth) best = { depth, uuid };
      const b = bodies?.[uuid];
      if (b && Array.isArray(b.children)) {
        b.children.forEach((c) => {
          if (typeof c === "string") stack.push({ uuid: c, depth: depth + 1 });
        });
      }
    }
    if (best.uuid) setEndEffectorChoice(best.uuid);
  }, [gripperTarget, selectedBody, bodies, worldBodies]);

  const effectiveEndEffectorUuid =
    endEffectorChoice === "gripper_target" ? gripperTarget?.bodyUuid || "" : endEffectorChoice;
  const useGripperTargetSite = endEffectorChoice === "gripper_target" && !!gripperTarget?.bodyUuid;

  const validateIkChain = () => {
    if (!effectiveEndEffectorUuid) {
      return "End-effector body is missing.";
    }
    const scene = getSceneState();
    const chain = buildIkChainFromScene(scene, effectiveEndEffectorUuid);
    if (chain) return "";
    return getLastIkChainBuildFailureReason() || "Unable to build IK chain from the current scene.";
  };

  useEffect(() => {
    const ids = (worldBodies || [])
      .map((wb) => (typeof wb === "string" ? wb : wb?.uuid))
      .filter(Boolean);
    if (!ids.length) return;
    if (!targetWorldUuid || !ids.includes(targetWorldUuid)) {
      setTargetWorldUuid(ids[0]);
    }
  }, [worldBodies, targetWorldUuid]);

  useEffect(() => {
    const ids = (worldBodies || [])
      .map((wb) => (typeof wb === "string" ? wb : wb?.uuid))
      .filter(Boolean);
    if (!ids.length) return;
    if (dropoffWorldUuid && !ids.includes(dropoffWorldUuid)) {
      setDropoffWorldUuid("");
    }
  }, [worldBodies, dropoffWorldUuid]);

  const clearTarget = () => {
    // Clear marker + cached target so it won't be reused across robots/scenes.
    ikTargetActions.clearMarkerWorld();
    window.dispatchEvent(new CustomEvent("ik-target-clear"));
  };

  const handleClose = () => {
    // Closing the panel should clear the marker + cached target.
    clearTarget();
    setIkWarning("");
    // Reset marker visibility to default for next open.
    ikTargetActions.setMarkerVisible(false);
    if (typeof onClose === "function") onClose();
  };

  useEffect(() => {
    let consecutiveFailures = 0;
    const onIkResult = (e) => {
      const detail = e?.detail || {};
      const status = detail.status;
      const error = Number(detail.error);
      if (status === "success") {
        consecutiveFailures = 0;
        setIkWarning("");
        return;
      }
      consecutiveFailures += 1;
      if (consecutiveFailures >= 10) {
        const suffix = Number.isFinite(error) ? ` (error ${error.toFixed(4)})` : "";
        setIkWarning(`IK target unreachable${suffix}`);
      }
    };
    window.addEventListener("ik-solve-result", onIkResult);
    return () => window.removeEventListener("ik-solve-result", onIkResult);
  }, []);

  useEffect(() => {
    let clearTimer = null;
    const messages = {
      PICK_MISSED: "Pick missed - retrying",
      APPROACH_TIMEOUT: "Approach timed out - retrying",
      TARGET_POSE_MISSING: "Target pose missing - retrying",
    };
    const onStatus = (e) => {
      const code = e?.detail?.code;
      const message = messages[code];
      if (!message) return;
      setPickPlaceStatus(message);
      if (clearTimer) window.clearTimeout(clearTimer);
      clearTimer = window.setTimeout(() => {
        setPickPlaceStatus("");
        clearTimer = null;
      }, 2500);
    };
    window.addEventListener("ik-pickplace-status", onStatus);
    return () => {
      if (clearTimer) window.clearTimeout(clearTimer);
      window.removeEventListener("ik-pickplace-status", onStatus);
    };
  }, []);

  const updateCoord = (idx, value) => {
    setEditCoords((prev) => {
      const next = [...prev];
      const num = Number(value);
      next[idx] = Number.isFinite(num) ? num : 0;
      return next;
    });
  };

  const dispatchTargetPicked = (world, bodyUuid = null, bodyName = null) => {
    const [x, y, z] = world || [];
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
    const validationError = validateIkChain();
    if (validationError) {
      setIkWarning(validationError);
      return;
    }
    setIkWarning("");
    ikTargetActions.setMarkerWorld([x, y, z]);
    window.dispatchEvent(
      new CustomEvent("ik-target-picked", {
        detail: {
          world: [x, y, z],
          bodyUuid,
          bodyName,
          dropoffBodyUuid: dropoffWorldUuid || null,
          dropoffBodyName: dropoffWorldUuid ? bodies?.[dropoffWorldUuid]?.name || null : null,
          endEffectorUuid: effectiveEndEffectorUuid || null,
          useGripperTargetSite,
        },
      })
    );
  };

  const applyManual = () => {
    const [x, y, z] = editCoords;
    dispatchTargetPicked([x, y, z]);
  };

  const worldBodyOptions = useMemo(() => {
    return (worldBodies || [])
      .map((wb) => {
        const uuid = typeof wb === "string" ? wb : wb?.uuid;
        const name =
          typeof wb === "string"
            ? bodies?.[wb]?.name
            : wb?.name || (uuid ? bodies?.[uuid]?.name : null);
        return uuid ? { uuid, name: name || uuid } : null;
      })
      .filter(Boolean);
  }, [worldBodies, bodies]);
  const targetHasGrasps = useMemo(() => {
    if (!targetWorldUuid) return false;
    const grasps = graspsByBodyUuid?.[targetWorldUuid];
    return Array.isArray(grasps) && grasps.length > 0;
  }, [graspsByBodyUuid, targetWorldUuid]);

  const resolveTargetWorldPosition = useCallback(
    (worldUuid) => {
      if (!worldUuid) return null;
      const targetObj = sceneObjects?.[worldUuid] || null;
      if (targetObj) {
        const bbox = new THREE.Box3().setFromObject(targetObj);
        if (!bbox.isEmpty()) {
          const center = new THREE.Vector3();
          bbox.getCenter(center);
          return [center.x, center.y, center.z];
        }
      }
      const fallback = bodies?.[worldUuid];
      if (Array.isArray(fallback?.position) && fallback.position.length === 3) {
        return [...fallback.position];
      }
      return null;
    },
    [bodies, sceneObjects]
  );

  // Keep marker/edit coordinates in sync with target-object dropdown selection.
  // This updates marker position only; it does not trigger an IK solve.
  useEffect(() => {
    if (!open) return;
    const target = resolveTargetWorldPosition(targetWorldUuid);
    if (!target) return;
    setEditCoords(target);
    ikTargetActions.setMarkerWorld(target);
  }, [open, targetWorldUuid, resolveTargetWorldPosition, ikTargetActions]);

  const applyTargetObject = () => {
    if (!targetWorldUuid) return;
    const target = resolveTargetWorldPosition(targetWorldUuid);
    if (!target) {
      logger.warn("[IK Panel] Unable to resolve target world position", { targetWorldUuid });
      return;
    }
    setEditCoords(target);
    dispatchTargetPicked(target, targetWorldUuid, bodies?.[targetWorldUuid]?.name || null);
  };

  return (
    <DraggablePanel
      title="IK Target Picker"
      titleIcon={titleIcon}
      open={open}
      onClose={handleClose}
      width={380}
      height={370}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="space-y-3 px-3 py-2 text-gray-200 text-sm">
        {ikWarning ? (
          <div className="rounded border border-yellow-700 bg-yellow-950/40 px-2 py-1 text-yellow-200 text-xs">
            {ikWarning}
          </div>
        ) : null}
        {pickPlaceStatus ? (
          <div className="rounded border border-sky-700 bg-sky-950/40 px-2 py-1 text-sky-200 text-xs">
            {pickPlaceStatus}
          </div>
        ) : null}
        <FieldLabel className="leading-5 select-none">
          Enter a world-space target and apply it. (Raycast picking has been removed.)
        </FieldLabel>
        <Separator />
        <Checkbox
          label="Show target marker"
          checked={markerVisible}
          onChange={(e) => {
            const visible = Boolean(e.target.checked);
            if (visible) {
              const validationError = validateIkChain();
              if (validationError) {
                setIkWarning(validationError);
                return;
              }
            }
            setIkWarning("");
            ikTargetActions.setMarkerVisible(visible);
            window.dispatchEvent(
              new CustomEvent("ik-target-marker-visibility", {
                detail: { visible },
              })
            );
          }}
          className="px-0 py-0"
        />
        <div className="space-y-2">
          <div className="text-gray-300 text-xs select-none">End effector</div>
          <MenuSelect
            value={endEffectorChoice}
            onChange={setEndEffectorChoice}
            options={[
              ...(gripperTarget
                ? [{ value: "gripper_target", label: "Gripper (gripper_target)" }]
                : []),
              ...Object.values(bodies || {}).map((b) => ({
                value: b.uuid,
                label: b.name || b.uuid,
              })),
            ]}
            contentClassName="max-h-[min(280px,50vh)]"
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="End effector body"
          />

          <div className="text-gray-300 text-xs select-none">Target object</div>
          <MenuSelect
            value={targetWorldUuid}
            onChange={setTargetWorldUuid}
            options={worldBodyOptions.map((entry) => ({ value: entry.uuid, label: entry.name }))}
            contentClassName="max-h-[min(280px,50vh)]"
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="Target object"
          />
          {targetHasGrasps ? (
            <Checkbox
              label="Show grasp poses"
              checked={showGrasps}
              onChange={() => toggleShowGrasps()}
              className="px-0 py-0"
            />
          ) : null}
          <div className="text-gray-300 text-xs select-none">Drop off target</div>
          <MenuSelect
            value={dropoffWorldUuid ? dropoffWorldUuid : "__none__"}
            onChange={(v) => setDropoffWorldUuid(v === "__none__" ? "" : v)}
            options={[
              { value: "__none__", label: "None" },
              ...worldBodyOptions.map((entry) => ({ value: entry.uuid, label: entry.name })),
            ]}
            contentClassName="max-h-[min(280px,50vh)]"
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="Drop off target"
          />
          <Button variant="secondary" size="small" onClick={applyTargetObject} className="w-full">
            Pick/Place target object
          </Button>

          <div className="text-gray-300 text-xs select-none">Edit target (world)</div>
          <div className="grid grid-cols-3 gap-2">
            {["X", "Y", "Z"].map((axis, idx) => {
              const inputKey = `coord-${idx}`;
              const baseValue = Number(editCoords[idx] ?? 0);
              const displayValue =
                editingInputs[inputKey] !== undefined
                  ? editingInputs[inputKey]
                  : formatCoord(baseValue);
              return (
                <div key={axis} className="space-y-1">
                  <AxisNumberInput
                    axis={axis}
                    value={displayValue}
                    dragValue={baseValue}
                    onDragValueChange={(nextValue) => updateCoord(idx, nextValue)}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: val }));
                      updateCoord(idx, val);
                    }}
                    onFocus={(e) => {
                      setEditingInputs((prev) => ({ ...prev, [inputKey]: e.target.value }));
                    }}
                    onBlur={(e) => {
                      const numVal = parseFloat(e.target.value);
                      updateCoord(idx, Number.isFinite(numVal) ? numVal : 0);
                      setEditingInputs((prev) => {
                        const next = { ...prev };
                        delete next[inputKey];
                        return next;
                      });
                    }}
                    labelClassName="text-xs panel-text-helper uppercase w-3 text-center"
                    inputClassName="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-gray-100 text-sm"
                    inputProps={{ step: "0.01" }}
                  />
                </div>
              );
            })}
          </div>
          <Button variant="secondary" size="small" onClick={applyManual} className="w-full">
            Apply coordinates
          </Button>
        </div>
      </div>
    </DraggablePanel>
  );
};

export default IkTargetPanel;
